import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { showMutationError } from "utils/showMutationError";

import {
  describeReplayOutcome,
  isAlreadyGone,
} from "../utils/deadLetterReplay";
import { DEAD_LETTERS_QUERY_KEY } from "./useDeadLetters";

/**
 * Puts a dead-lettered message back in the queue to be parsed again.
 *
 * A 200 does not mean it worked. The endpoint answers 200 with `replayed: false` and a
 * `failureReason` when the message still does not parse, which is the normal outcome until
 * whatever could not be modelled is fixed — reporting that as success is the single easiest
 * way to get this integration wrong.
 *
 * A 404 means it is no longer a dead letter of this tenant: a stale id, or someone replayed
 * it first. That is "already gone", not a failure, so the list is refreshed and nothing is
 * shouted at the user.
 *
 * On success the row leaves this list and becomes an ordinary unprocessed message. It is not
 * filed anywhere yet — the inbound poll picks it up, by default every 15 minutes — so the
 * message deliberately does not claim the message has landed.
 */
export const useReplayDeadLetter = () => {
  const queryClient = useQueryClient();
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [DEAD_LETTERS_QUERY_KEY] });

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (id) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/healthlink/messages/dead-letters/${id}/replay`
      );

      return response.data;
    },
    onSuccess: (result) => {
      const { tone, message, shouldRefresh } = describeReplayOutcome(result);

      if (tone === "success") {
        toast.success(message);
      } else {
        toast.error(message);
      }

      if (shouldRefresh) invalidate();
    },
    onError: (error) => {
      if (isAlreadyGone(error)) {
        toast.success("That message is no longer a dead letter.");
        invalidate();

        return;
      }

      showMutationError(error, "Could not replay the message.");
    },
  });
};
